import { useSyncExternalStore, type ReactNode } from 'react';
import type { BenchMetricKey, PortfolioBoard, PortfolioEntry } from '@aithora/shared';
import { sx } from '../../ui/designStyles';
import { usePerf, type PerfCtx } from './ctx';
import { COLS, gradeOf, pct100, pre } from './lib';

/**
 * What Portfolio, Distribution and Trajectory share (design isBenchTab, T16456-16469):
 * the sub-header ("9 of 10 facilities graded against national peers (n=14,709)", the beds
 * filter) and the view state the design keeps across the three (openRow, sortBy, qBench,
 * distMetric), so a building clicked on Distribution opens expanded on Portfolio.
 */

export interface BenchUi {
  openRow: string | null;
  sortBy: 'comp' | 'leak' | 'beds' | 'name';
  qBench: string;
  distMetric: number | null;
}
let ui: BenchUi = { openRow: null, sortBy: 'comp', qBench: '', distMetric: null };
const subs = new Set<() => void>();
export function setBenchUi(patch: Partial<BenchUi>) {
  ui = { ...ui, ...patch };
  subs.forEach((f) => f());
}
/** For tests: back to the design's opening state. */
export function resetBenchUi() {
  setBenchUi({ openRow: null, sortBy: 'comp', qBench: '', distMetric: null });
}
export function useBenchUi(): BenchUi {
  return useSyncExternalStore(
    (f) => {
      subs.add(f);
      return () => subs.delete(f);
    },
    () => ui,
    () => ui,
  );
}

/** A graded building as the tabs use it: the percentiles in the design's column order. */
export interface BenchRow {
  id: string;
  name: string;
  ccn: string;
  beds: number;
  comp: number;
  grade: string;
  cells: (number | null)[];
  delta: number | null;
  upsideUsd: number | null;
  leakYr: number | null;
  capped: boolean;
}

export const cellsOf = (m: PortfolioEntry['metrics'] | undefined | null): (number | null)[] =>
  COLS.map((c) => pct100(m?.[c.key]?.percentile));

/** The rows in view (design shown, L35075): scored, the beds filter, the search, the sort. */
export function benchRows(ctx: PerfCtx, u: BenchUi): { shown: BenchRow[]; unscored: PortfolioEntry[]; total: number } {
  const board = ctx.board?.data as PortfolioBoard | null | undefined;
  const list = board?.facilities ?? [];
  const q = u.qBench.trim().toLowerCase();
  const bedOk = (b: number) => ctx.beds === 'All' || (ctx.beds === '<100' ? b < 100 : b >= 100);
  const nameOk = (e: PortfolioEntry) => !q || e.name.toLowerCase().includes(q) || (e.ccn ?? '').includes(q);
  const shown = list
    .filter((e) => e.composite != null && bedOk(e.beds ?? 0) && nameOk(e))
    .map((e): BenchRow => {
      const cells = cellsOf(e.metrics);
      return {
        id: e.id,
        name: e.name,
        ccn: e.ccn ?? '',
        beds: e.beds ?? 0,
        comp: Math.round(e.composite as number),
        grade: e.grade ?? gradeOf(e.composite as number),
        cells,
        delta: e.delta ?? null,
        upsideUsd: e.upsideUsd ?? null,
        leakYr: e.leakageUsdYear == null ? null : Math.round(e.leakageUsdYear / 1000),
        capped: cells[7] !== null && (cells[7] as number) < 20,
      };
    })
    .sort((a, b) => {
      if (u.sortBy === 'leak') return (b.leakYr ?? -1) - (a.leakYr ?? -1);
      if (u.sortBy === 'name') return a.name.localeCompare(b.name);
      if (u.sortBy === 'beds') return b.beds - a.beds;
      return b.comp - a.comp;
    });
  return { shown, unscored: list.filter((e) => e.composite == null), total: board?.totalCount ?? list.length };
}

/** The Facility Admin's own building as a row (design bScope, L35103). */
export function ownRow(ctx: PerfCtx): BenchRow | null {
  const f = ctx.fac;
  if (!f || f.composite == null) return null;
  const cells = cellsOf(f.metrics);
  return {
    id: f.orgId ?? 'own',
    name: f.name,
    ccn: f.ccn ?? '',
    beds: f.beds ?? 0,
    comp: Math.round(f.composite),
    grade: f.grade ?? gradeOf(f.composite),
    cells,
    delta: null,
    upsideUsd: null,
    leakYr: null,
    capped: cells[7] !== null && (cells[7] as number) < 20,
  };
}

/** Which source a column reads from: the server says, else CMS (every scorer metric is CMS). */
export const srcOf = (board: PortfolioBoard | null | undefined, key: BenchMetricKey) => (board?.metricSources?.[key] === 'live' ? 'LIVE' : 'CMS');

/** Tab frame: the sub-header and the scroller (design T16457-16469). */
export function BenchFrame({ label, children }: { label: 'Portfolio' | 'Distribution' | 'Trajectory'; children: ReactNode }) {
  const ctx = usePerf();
  const u = useBenchUi();
  const { shown, total } = benchRows(ctx, u);
  const summary = ctx.op ? shown.length + ' of ' + total + ' facilities graded against ' + ctx.peerLabel : 'Your building graded against ' + ctx.peerLabel;
  const busy = ctx.op ? !!ctx.board?.loading && !ctx.board?.data : ctx.facState.loading;
  return (
    <div data-screen-label={label} style={sx('flex:1 1 auto;min-height:0;display:flex;flex-direction:column;min-width:0;')} aria-busy={busy ? 'true' : undefined} data-testid={'bench-' + label.toLowerCase()}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;min-width:0;padding-top:6px;')}>
        <span style={sx('font-size:16px;color:#54606C;min-width:0;flex:1 1 240px;')}>
          <span>{summary}</span>
        </span>
        {ctx.op && (
          <>
            <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.14em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Beds</span>
            <div role="group" aria-label="Beds" style={sx('display:inline-flex;border:1px solid rgba(34,39,46,.16);border-radius:8px;overflow:hidden;background:#FCFAF4;')}>
              {(['All', '<100', '100+'] as const).map((b) => {
                const on = ctx.beds === b;
                return (
                  <a
                    key={b}
                    href="#"
                    aria-pressed={on}
                    onClick={(e) => {
                      pre(e);
                      ctx.setBeds(b);
                      setBenchUi({ openRow: null });
                    }}
                    style={sx('display:inline-block;padding:6px 12px;font-size:16px;font-weight:' + (on ? '700' : '500') + ';text-decoration:none;color:' + (on ? '#fff' : '#54606C') + ';background:' + (on ? '#3C454F' : 'transparent') + ';')}
                  >
                    {b}
                  </a>
                );
              })}
            </div>
          </>
        )}
      </div>
      <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;min-width:0;display:block;padding-right:3px;')}>
        {ctx.facState.error && !(ctx.op ? ctx.board?.data : ctx.fac) ? (
          <div role="alert" style={sx('font-size:16px;color:#B3261E;padding:14px 0;')}>
            {ctx.facState.error}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
